import { useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { bumpMyGamesPlayed } from '../../../lib/api/profile'
import { fetchAiPlayers } from '../../../lib/api/ai'
import type { AiPlayerRow } from '../../../lib/database.types'
import { INGREDIENT_BY_ID } from '../data/ingredientCards'
import { IngredientCardImage } from '../components/IngredientCardImage'
import { StoryBuilderScreen, type CourseData } from '../screens/StoryBuilderScreen'
import { PassPlaySetupScreen } from '../../passplay/PassPlaySetupScreen'
import { generatePassplayCourse } from '../../../lib/ai/passplayAi'
import {
  castVote, courseKeyOf, createState, readyForCourse, readyToVote, readyToWrite,
  remainingHand, startPresentingCourse, startWriting, submitCourse, totalScores,
  type IetPassPlayState,
} from './state'
import { useT } from '../../../i18n'

const COURSE_NAMES = ['Appetizer', 'Main', 'Dessert'] as const
const COURSE_EMOJIS = ['🥗', '🍝', '🍰'] as const

interface Props {
  onExit: () => void
}

export function IdEatThatPassPlayApp({ onExit }: Props) {
  const t = useT()
  const [aiPool, setAiPool] = useState<AiPlayerRow[] | null>(null)
  const [state, setState] = useState<IetPassPlayState | null>(null)

  useEffect(() => {
    fetchAiPlayers().then(setAiPool).catch(() => setAiPool([]))
  }, [])

  // Count each finished pass-play game once toward the lifetime
  // Games Played stat (local games never touch the server otherwise).
  // The ref re-arms when a new game starts so "play again" counts too.
  const bumpedRef = useRef(false)
  useEffect(() => {
    if (state?.phase === 'results') {
      if (!bumpedRef.current) { bumpedRef.current = true; bumpMyGamesPlayed() }
    } else {
      bumpedRef.current = false
    }
  }, [state?.phase])

  if (!state) {
    if (!aiPool) {
      return (
        <div className="screen items-center justify-center">
          <p className="text-brand-muted">{t('Loading…')}</p>
        </div>
      )
    }
    return (
      <PassPlaySetupScreen
        title="I'd Eat That"
        primaryButtonClass="btn-iet"
        aiPool={aiPool.map(a => ({
          id: a.id, name: a.name, avatarEmoji: a.avatar_emoji,
        }))}
        minPlayers={2}
        maxPlayers={6}
        onStart={(players) => setState(createState(players))}
        onBack={onExit}
      />
    )
  }
  return <Game state={state} setState={setState} onExit={onExit} />
}

function Game({
  state, setState, onExit,
}: {
  state: IetPassPlayState
  setState: React.Dispatch<React.SetStateAction<IetPassPlayState | null>>
  onExit: () => void
}) {
  const t = useT()
  const player = state.players[state.currentPlayerIdx]
  const presenter = state.players[state.presenterIdx]
  const voter = state.players[state.currentVoterIdx]
  const subtitle = useMemo(() => t("Phone Pass Play · I\'d Eat That"), [])
  const courseName = COURSE_NAMES[state.currentCourse - 1]
  const courseEmoji = COURSE_EMOJIS[state.currentCourse - 1]

  // ── Online AI upgrade: swap each AI course's template text for a
  // Claude Haiku line while the humans are still writing. Guarded so a
  // course that's already being presented is never rewritten mid-read;
  // offline or on any error the template text simply stays.
  const upgradeFiredRef = useRef(false)
  useEffect(() => {
    if (upgradeFiredRef.current) return
    upgradeFiredRef.current = true
    const ais = state.players.filter(p => p.kind === 'ai')
    for (const ai of ais) {
      const meal = state.meals[ai.id]
      for (const course of [1, 2, 3] as const) {
        const entry = meal[courseKeyOf(course)]
        if (!entry) continue
        const cardNames = entry.ids.map(id => INGREDIENT_BY_ID[id]?.name ?? id)
        void generatePassplayCourse({
          personaName: ai.name,
          aiPlayerId: ai.aiPlayerId,
          course,
          cardNames,
        }).then(text => {
          if (!text) return
          setState(prev => {
            if (!prev) return prev
            // Only replace while this course can't be on screen yet:
            // a strictly-later course, or the current course while the
            // table is still in its writing phases.
            const writingPhases = ['intro', 'pass_to_write', 'write_course', 'course_all_in']
            const safe = course > prev.currentCourse
              || (course === prev.currentCourse && writingPhases.includes(prev.phase))
            if (!safe) return prev
            const k = courseKeyOf(course)
            const cur = prev.meals[ai.id][k]
            if (!cur) return prev
            return {
              ...prev,
              meals: {
                ...prev.meals,
                [ai.id]: { ...prev.meals[ai.id], [k]: { ...cur, text } },
              },
            }
          })
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (state.phase === 'intro') {
    return (
      <Shell title="I'd Eat That" subtitle={subtitle} onBack={onExit}>
        <CenterCard
          headline={t('7 cards each. One course at a time.')}
          body={t("Everyone writes their appetizer first, then each one gets presented and voted on. Then mains, then desserts. Most 'I'd eat that!' votes wins.")}
          buttonLabel={t('Deal cards')}
          onTap={() => setState(startWriting(state))}
        />
      </Shell>
    )
  }

  if (state.phase === 'pass_to_write' && player) {
    return (
      <PassToPlayerSplash
        playerName={player.name}
        instruction={t('Write your {course} using your remaining cards.', { course: courseName.toLowerCase() })}
        onReady={() => setState(readyToWrite(state))}
      />
    )
  }

  if (state.phase === 'write_course' && player) {
    return (
      <WriteCourseScreen
        key={`${player.id}-${state.currentCourse}`}
        playerName={player.name}
        course={state.currentCourse}
        cards={remainingHand(state.meals[player.id])}
        onSubmit={(entry) => setState(submitCourse(state, entry))}
        onBack={onExit}
      />
    )
  }

  if (state.phase === 'course_all_in') {
    return (
      <Shell title="I'd Eat That" subtitle={subtitle} onBack={onExit}>
        <CenterCard
          headline={t("Everyone's {course} is in.", { course: courseName.toLowerCase() })}
          body={t('{emoji} Now each {course} gets presented, and everyone votes on it.', { emoji: courseEmoji, course: courseName.toLowerCase() })}
          buttonLabel={t('Start presenting')}
          onTap={() => setState(startPresentingCourse(state))}
        />
      </Shell>
    )
  }

  if (state.phase === 'present_intro' && presenter) {
    const course = state.meals[presenter.id][courseKeyOf(state.currentCourse)]
    return (
      <Shell title="I'd Eat That" subtitle={`${presenter.name} · ${courseName}`} onBack={onExit}>
        <div className="w-full max-w-sm space-y-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-game-iet-dark">
            {t('Course {n} · {course}', { n: state.currentCourse, course: courseName })}
          </p>
          <h2 className="text-3xl font-bold text-brand-text">{presenter.name}</h2>
          <div className="card p-5 text-left">
            <p className="text-sm text-brand-muted mb-2">
              {courseEmoji} {courseName}
            </p>
            <p className="text-brand-text leading-relaxed">"{course?.text ?? '…'}"</p>
            {(course?.ids.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {course!.ids.map(id => (
                  <IngredientCardImage key={id} id={id} size="sm" />
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setState(readyForCourse(state))}
            className="btn-iet w-full"
          >{t('Start voting')}</button>
        </div>
      </Shell>
    )
  }

  if (state.phase === 'pass_to_vote' && voter) {
    return (
      <PassToPlayerSplash
        playerName={voter.name}
        instruction={t("Would you eat {name}'s {course}?", { name: presenter?.name, course: courseName.toLowerCase() })}
        onReady={() => setState(readyToVote(state))}
      />
    )
  }

  if (state.phase === 'vote_course' && voter && presenter) {
    const course = state.meals[presenter.id][courseKeyOf(state.currentCourse)]
    return (
      <Shell title="I'd Eat That" subtitle={t('{name} voting', { name: voter.name })} onBack={onExit}>
        <div className="w-full max-w-sm space-y-5">
          <div className="card p-5">
            <p className="text-xs font-semibold text-game-iet-dark uppercase tracking-wide mb-2">
              {presenter.name}'s {courseName}
            </p>
            <p className="text-brand-text leading-relaxed">"{course?.text ?? '…'}"</p>
            {(course?.ids.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {course!.ids.map(id => (
                  <IngredientCardImage key={id} id={id} size="sm" />
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setState(castVote(state, true))}
              className="card p-5 border-2 border-brand-success/40 bg-brand-success/10 text-brand-success font-bold active:scale-95 transition-transform"
            >
              <div className="text-3xl mb-1">✅</div>{t("I'd eat that!")}</button>
            <button
              onClick={() => setState(castVote(state, false))}
              className="card p-5 border-2 border-brand-danger/40 bg-brand-danger/10 text-brand-danger font-bold active:scale-95 transition-transform"
            >
              <div className="text-3xl mb-1">🙅</div>{t('No thanks')}</button>
          </div>
        </div>
      </Shell>
    )
  }

  if (state.phase === 'results') {
    const scores = totalScores(state)
    const ranked = state.players.slice().sort((a, b) => (scores[b.id] ?? 0) - (scores[a.id] ?? 0))
    const winner = ranked[0]
    return (
      <Shell title="I'd Eat That" subtitle={t('Final results')} onBack={onExit}>
        <div className="w-full max-w-sm space-y-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-game-iet-dark">
            {t('Winner')}
          </p>
          <h1 className="font-display text-5xl font-black text-brand-text">{winner?.name}</h1>
          <p className="text-lg text-brand-muted">
            {t("{n} I'd-eat-thats", { n: scores[winner?.id ?? ''] ?? 0 })}
          </p>
          <ol className="space-y-2 text-left mt-6">
            {ranked.map((p, i) => (
              <li key={p.id} className="card p-3 flex items-center justify-between">
                <span className="flex items-center gap-3">
                  <span className="font-display text-2xl font-black text-game-iet-dark w-6">
                    {i + 1}.
                  </span>
                  <span className="font-semibold text-brand-text">{p.name}</span>
                </span>
                <span className="text-sm text-brand-muted">
                  {scores[p.id] ?? 0}
                </span>
              </li>
            ))}
          </ol>
          <button onClick={onExit} className="btn-iet w-full mt-6">{t('Done')}</button>
        </div>
      </Shell>
    )
  }

  return (
    <Shell title="I'd Eat That" subtitle="…" onBack={onExit}>
      <p className="text-brand-muted">{t('Unexpected state.')}</p>
    </Shell>
  )
}

// ─── Bits ────────────────────────────────────────────────────────────

function Shell({
  title, subtitle, onBack, children,
}: { title: string; subtitle: string; onBack: () => void; children: React.ReactNode }) {
  return (
    <div className="screen">
      <BackgroundTitle text="I'd Eat That" color="#B58615" />
      <PageHeader title={title} subtitle={subtitle} onBack={onBack} />
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-6 relative z-10">
        {children}
      </div>
    </div>
  )
}

function CenterCard({
  headline, body, buttonLabel, onTap,
}: { headline: string; body: string; buttonLabel: string; onTap: () => void }) {
  return (
    <div className="text-center max-w-sm space-y-5">
      <h2 className="text-2xl font-bold text-brand-text leading-tight">{headline}</h2>
      <p className="text-brand-muted leading-relaxed">{body}</p>
      <button onClick={onTap} className="btn-iet w-full">{buttonLabel}</button>
    </div>
  )
}

function PassToPlayerSplash({
  playerName, instruction, onReady,
}: { playerName: string; instruction: string; onReady: () => void }) {
  const t = useT()
  return (
    <div className="screen items-center justify-center bg-game-iet-light">
      <BackgroundTitle text="I'd Eat That" color="#B58615" />
      <div className="text-center max-w-sm px-6 space-y-5 relative z-10">
        <p className="text-xs font-semibold uppercase tracking-widest text-game-iet-dark">{t('Pass the phone to')}</p>
        <h1 className="font-display text-5xl font-black text-brand-text">{playerName}</h1>
        <p className="text-brand-muted">{instruction}</p>
        <button onClick={onReady} className="btn-iet w-full mt-4">
          {t("I'm {name}, start", { name: playerName })}
        </button>
      </div>
    </div>
  )
}

/**
 * Write ONE course using the SAME StoryBuilderScreen the online room
 * uses: real ingredient-card art, tap the cards you're cooking with
 * (≥1, same rule as live), dessert must use everything left. The
 * player — not an auto-split — decides which cards go in which course,
 * and the phone comes back for the next course after this one is
 * presented and voted (course-major, exactly like the live game).
 */
function WriteCourseScreen({
  playerName, course, cards, onSubmit, onBack,
}: {
  playerName: string
  course: 1 | 2 | 3
  cards: string[]
  onSubmit: (entry: { text: string; ids: string[] }) => void
  onBack: () => void
}) {
  const courseName = COURSE_NAMES[course - 1]
  return (
    <div className="screen">
      <BackgroundTitle text="I'd Eat That" color="#B58615" />
      <PageHeader
        title="I'd Eat That"
        subtitle={`${playerName} · ${COURSE_EMOJIS[course - 1]} ${courseName}`}
        onBack={onBack}
      />
      <StoryBuilderScreen
        key={course}
        hand={cards}
        courseIndex={(course - 1) as 0 | 1 | 2}
        mustUseAll={course === 3}
        onSubmit={(data: CourseData) => onSubmit({ text: data.text, ids: data.usedCards })}
      />
    </div>
  )
}
